import { useMutation, useQuery } from "@apollo/client/react";
import { CameraOutlined, CloseOutlined, DeleteOutlined, MenuOutlined } from "@ant-design/icons";
import {
  Alert,
  App,
  Button,
  Drawer,
  Empty,
  Flex,
  FloatButton,
  Image,
  Masonry,
  Result,
  Typography,
  Upload,
  type UploadProps,
} from "antd";
import { useCallback, useState } from "react";
import { useUploadProgress } from "../../hooks/useUploadProgress.ts";
import { useTranslation } from "react-i18next";
import {
  DeleteStagingFileDocument,
  GetCurrentLoggedUserDocument,
  GetStagingFilesDocument,
  type StagingFileItemFragment,
} from "../../gql/generated/graphqlSchema.tsx";
import { DeletableImageFile } from "../../components/DeletableImageFile.tsx";
import { dateOverlay } from "../../components/DeletableImageFile.styles.ts";
import { LanguageSwitcher } from "../../components/LanguageSwitcher.tsx";
import { PageLoader } from "../../components/PageLoader.tsx";
import { usePageVisible } from "../../hooks/usePageVisible.ts";
import { title } from "../../styles/shared.styles.ts";
import { formatDateTime } from "../../utils/datetime.ts";
import { MAX_UPLOAD_SIZE_BYTES, uploadResponseSchema } from "../../utils/upload.ts";
import { css } from "@linaria/core";

const page = css`
  min-height: 100vh;
  min-height: 100dvh;
  background: var(--ant-color-bg-layout);
`;

const topBar = css`
  position: sticky;
  top: 0;
  z-index: 10;
  height: 48px;
  padding: 0 var(--ant-padding-xs) 0 var(--ant-padding-md);
  background: var(--ant-color-bg-container);
  border-bottom: 1px solid var(--ant-color-border-secondary);
`;

const content = css`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: var(--ant-padding-sm);
`;

const emptyState = css`
  flex: 1;
`;

const drawerItem = css`
  padding: var(--ant-padding-sm);
  border-bottom: 1px solid var(--ant-color-border-secondary);
`;

const drawerBody = css`
  padding: 0;
`;

const drawerHeader = css`
  padding: var(--ant-padding-sm);
`;

const fabRoot = css`
  width: 160px;
  height: 56px;
  bottom: 16px;
  right: 16px;
`;

const fabContent = css`
  font-size: var(--ant-font-size);
`;

const fabIcon = css`
  font-size: var(--ant-font-size-lg);
`;

export const PhoneUploadPage = () => {
  const { t, i18n } = useTranslation();
  const { message, modal } = App.useApp();
  const visible = usePageVisible();
  const { data: userData, loading: userLoading } = useQuery(GetCurrentLoggedUserDocument);
  const loggedIn = !!userData?.getCurrentLoggedUser;
  const { data: stagingData, refetch: refetchStaging } = useQuery(GetStagingFilesDocument, {
    skip: !loggedIn,
    pollInterval: loggedIn && visible ? 5000 : 0,
  });
  const [deleteStagingFile] = useMutation(DeleteStagingFileDocument);

  const { progress: uploadPercent, uploading, trackFile, completeFile, failFile } = useUploadProgress();
  const [menuOpen, setMenuOpen] = useState(false);

  const handleChange: UploadProps["onChange"] = ({ file }) => {
    if (file.status === "uploading") {
      trackFile(file.uid, Math.round(file.percent ?? 0));
    }
    if (file.status === "done") {
      completeFile(file.uid);
      const parsed = uploadResponseSchema.safeParse(file.response);
      if (parsed.success) {
        void refetchStaging();
      }
    }
    if (file.status === "error") {
      failFile(file.uid);
      void message.error(t("phone.uploadFailed"));
    }
  };

  const handleDeleteAll = useCallback(() => {
    modal.confirm({
      title: t("phone.deleteAllTitle"),
      content: t("phone.deleteAllConfirm"),
      okButtonProps: { danger: true, icon: <DeleteOutlined /> },
      cancelButtonProps: { icon: <CloseOutlined /> },
      okText: t("phone.deleteAll"),
      onOk: async () => {
        const files = stagingData?.stagingFiles ?? [];
        for (const file of files) {
          await deleteStagingFile({ variables: { id: file.id } });
        }
        void refetchStaging();
      },
    });
  }, [deleteStagingFile, modal, refetchStaging, stagingData?.stagingFiles, t]);

  const renderItem = useCallback(
    (itemInfo: { data: StagingFileItemFragment }) => {
      const file = itemInfo.data;
      return (
        <DeletableImageFile
          file={file}
          onDelete={() => deleteStagingFile({ variables: { id: file.id }, refetchQueries: [GetStagingFilesDocument] })}
        >
          <div className={dateOverlay}>{formatDateTime(file.createdAt, i18n.language)}</div>
        </DeletableImageFile>
      );
    },
    [deleteStagingFile, i18n.language],
  );

  const loggedOut = !userLoading && !userData?.getCurrentLoggedUser;
  const stagingFiles = stagingData?.stagingFiles ?? [];

  const masonryItems = stagingFiles.map((file) => ({
    key: file.id,
    data: file,
  }));

  const renderContent = () => {
    if (userLoading) return <PageLoader />;
    if (loggedOut) {
      return (
        <Flex align="center" className={emptyState} justify="center">
          <Result status="warning" title={t("phone.notLoggedIn")} />
        </Flex>
      );
    }
    if (stagingFiles.length === 0) {
      return (
        <Flex align="center" className={emptyState} justify="center">
          <Empty description={t("phone.noPhotos")} />
        </Flex>
      );
    }
    return (
      <Flex gap="middle" vertical>
        <Alert closable showIcon title={t("phone.hint")} type="info" />
        <Image.PreviewGroup>
          <Masonry columns={2} gutter={[4, 4]} itemRender={renderItem} items={masonryItems} />
        </Image.PreviewGroup>
      </Flex>
    );
  };

  return (
    <Flex className={page} vertical>
      <Flex align="center" className={topBar} justify="space-between">
        <Typography.Title className={title} level={4}>
          Calculabel
        </Typography.Title>
        <Button icon={<MenuOutlined />} onClick={() => setMenuOpen(true)} type="text" />
      </Flex>

      <div className={content}>
        {renderContent()}

        {!loggedOut && (
          <Upload
            action="/i/upload/staging"
            beforeUpload={(file) => {
              if (file.size > MAX_UPLOAD_SIZE_BYTES) {
                void message.error(t("ingredients.photos.tooLarge"));
                return Upload.LIST_IGNORE;
              }
              return true;
            }}
            multiple
            onChange={handleChange}
            showUploadList={false}
          >
            <FloatButton
              classNames={{ root: fabRoot, content: fabContent, icon: fabIcon }}
              content={uploading ? `${t("phone.uploading")} ${uploadPercent}%` : t("phone.upload")}
              icon={<CameraOutlined />}
              shape="square"
              type="primary"
            />
          </Upload>
        )}
      </div>

      <Drawer
        classNames={{ body: drawerBody, header: drawerHeader }}
        destroyOnHidden
        onClose={() => setMenuOpen(false)}
        open={menuOpen}
        placement="right"
        size={280}
        title="Calculabel"
      >
        <Flex className={drawerItem} gap="small" vertical>
          <Typography.Text>
            {t("phone.recentPhotos")}: <strong>{stagingFiles.length}</strong>
          </Typography.Text>
          <Button block danger disabled={stagingFiles.length === 0} icon={<DeleteOutlined />} onClick={handleDeleteAll}>
            {t("phone.deleteAll")}
          </Button>
        </Flex>
        <div className={drawerItem}>
          <LanguageSwitcher />
        </div>
      </Drawer>
    </Flex>
  );
};
